import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Col,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import {
  useForm,
  FieldErrors,
  useFieldArray,
  Controller,
  useWatch,
} from "react-hook-form";
import CommonForm from "../../../../Components/Common/CommonForm";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { useMasterPeriodService } from "modules/Library/Period/api/usePeriodService";
import { useAliquotQuery } from "modules/Library/Aliquote/api/useAliquotService";
import ChecklistAssignmentStep from "../components/ChecklistAssignmentStep";
import CRFAssignmentStep from "../components/CRFAssignmentStep";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import { useCRFService } from "modules/Library/CRF/api/useCRFQuery";
import { buildStudyChecklistsPayload, buildStudyCRFsPayload, dynamicFieldsEdit, getChangedFields, periodColumnsEdit, refDrugFieldNames, testDrugFieldNames } from "../helpers/studyconstant";
import {
  EditStudyDetailsFormProps,
  StudyFormValues,
} from "../helpers/study.types";
import { getCurrentTabFields, visitformfields } from "../helpers/studyconstant";
import { parseLocalDate, showToastMessage } from "common/service/CommonService";
import CRFScreeningModal from "modules/Volunteer/AssigningStudyModule/StudyScreening/components/CRFScreeningModal";
import { loadCodelistsDetailId } from "api/modules/common.option";
import { PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { validations } from "validations/FormValidations";

/* ---------- Component ---------- */
const EditStudyForm: React.FC<EditStudyDetailsFormProps> = ({
  existingData,
  onSave,
  onCancel,
  activeSection,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
    trigger,
    setError,
    clearErrors,
    unregister,
  } = useForm<StudyFormValues>({
    defaultValues: {
      ...existingData,
      periods: existingData?.periods || [],
    },
    mode: "onBlur",
  });

  const numPeriods = watch("numberofperiods");

  /* ---------- Ambulatory Visit States & Forms ---------- */
  const [deletedVisitIds, setDeletedVisitIds] = useState<number[]>([]);
  // Fetch CRFs at main form level and pass query to CRFAssignmentStep
  const { AssignCRFSelectAllQuery: studyCRFAssignmentQuery } = useCRFService({
    moduleid: null,
    modulename: null,
    categorynames: ["study"],
    pageNumber: 1,
    pageSize: 1000,
    enabledSelectAll: false,
    enabledCategorySelectAll: false,
    enabledAssignCRFSelectAll: true,
  });


  const {
    control: visitControl,
    handleSubmit: handleVisitSubmit,
    setValue: setVisitValue,
    getValues: getVisitValues,
    reset: resetVisit,
    formState: { errors: visitErrors },
    trigger: triggerVisit,
    clearErrors: clearVisitErrors,
    unregister: unregisterVisit,
  } = useForm<any>({
    defaultValues: {
      visitname: "",
      visittype: "",
      visitcategory: "",
      ambulatorytotalsamplingpoints: "",
      samplingpoints: [],
      deviation: "",
      visitdescription: "",
    },
    mode: "onSubmit",
  });

  const watchedAmbulatoryTotalSamplingPoints = useWatch({
    control: visitControl,
    name: "ambulatorytotalsamplingpoints",
  });

  const [ambulatorySamplingPointsUI, setAmbulatorySamplingPointsUI] = useState<any[]>([]);

  useEffect(() => {
    if (!watchedAmbulatoryTotalSamplingPoints || Number(watchedAmbulatoryTotalSamplingPoints) <= 0) {
      setAmbulatorySamplingPointsUI([]);
      return;
    }

    const count = Number(watchedAmbulatoryTotalSamplingPoints) || 0;
    setAmbulatorySamplingPointsUI(Array.from({ length: count }, (_, i) => i + 1));

    const currentPoints = getVisitValues("samplingpoints") || [];
    if (Array.isArray(currentPoints) && currentPoints.length > count) {
      for (let i = count; i < currentPoints.length; i++) {
        unregisterVisit(`samplingpoints.${i}` as any);
      }
      setVisitValue("samplingpoints", currentPoints.slice(0, count));
    }
  }, [watchedAmbulatoryTotalSamplingPoints]);

  const ambulatoryFormFields = useMemo(() => {
    return getCurrentTabFields(12);
  }, []);

  // Fetch checklist master data for the Checklist Assignment edit section
  const { SelectAllQuery: checklistMasterQuery } = useCheckListMasterService({
    checklistcategory: "study",
    ispagination: false,
    typeEnabled: false,
  });
  const rawChecklistData: any[] = (checklistMasterQuery?.data as any)?.data || [];
  const [samplingpointsUI, setSamplingPointsUI] = useState<any[]>([]);

  // Fetch all master period records for display sequence dropdown
  const { SelectAllQuery: masterPeriodSelectAll } = useMasterPeriodService(
    "",
    undefined,
    1,
    1000,
  );

  const masterPeriodData: any[] =
    (masterPeriodSelectAll as any)?.data?.data ?? [];
  const displaySequenceOptions = masterPeriodData.map((item: any) => ({
    label: String(item.period_number),
    value: String(item.period_number),
  }));
  // Fetch all master period records for display sequence dropdown
  const { SelectAllQuery: masterAliquoteSelectAll } = useAliquotQuery(
    "",
    1,
    1000,
  );

  const masterAliquoteData: any[] =
    (masterAliquoteSelectAll as any)?.data?.data ?? [];
  const displayAliquoteSequenceOptions = masterAliquoteData.map(
    (item: any) => ({
      label: String(item.aliquot_number),
      value: String(item.aliquot_number),
    }),
  );

  const totalSamplingPoints = watch("totalsamplingpoints");
  /* ---------- Period Field Array ---------- */

  const {
    fields: periodFields,
    append,
    replace,
    remove,
  } = useFieldArray({
    control,
    name: "periods",
  });

  /* ---------- Reset Form ---------- */

  useEffect(() => {
    if (existingData) {
      reset({
        ...existingData,
        periods:
          existingData?.periods?.map((p: any) => ({
            ...p,
            start_date: p.start_date || "",
            checkin_date: p.checkin_date || "",
            checkout_date: p.checkout_date || "",
          })) || [],
      });

      if (existingData.visits && existingData.visits.length > 0) {
        const visit = existingData.visits[0];

        const visitMap = new Map<string, any[]>();
        existingData.visits.forEach((v: any) => {
          const vNum = String(v.visitnumber || v.visitname || v.visitid).trim();
          if (!visitMap.has(vNum)) {
            visitMap.set(vNum, []);
          }
          visitMap.get(vNum)!.push(v);
        });

        const sortedVNums = Array.from(visitMap.keys()).sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
        );

        const spList: string[] = [];
        sortedVNums.forEach((vNum) => {
          const vList = visitMap.get(vNum) || [];
          if (vList.length > 0) {
            const v = vList[0];
            if (Array.isArray(v.samplingpoints)) {
              v.samplingpoints.forEach((sp: any) => {
                if (sp && !spList.includes(String(sp).trim())) spList.push(String(sp).trim());
              });
            } else if (v.samplingpoints && !spList.includes(String(v.samplingpoints).trim())) {
              spList.push(String(v.samplingpoints).trim());
            }
          }
        });

        spList.sort((a, b) => Number(a) - Number(b));

        resetVisit({
          visitname: visit.visitname || "",
          visittype: visit.visittype || "",
          visitcategory: visit.visitcategory || "",
          ambulatorytotalsamplingpoints: spList.length || visit.ambulatorytotalsamplingpoints || "",
          samplingpoints: spList,
          deviation: visit.visitwindowdeviation || visit.deviation || "",
          visitdescription: visit.visitdescription || "",
        });
      } else {
        resetVisit({
          visitname: "",
          visittype: "",
          visitcategory: "",
          ambulatorytotalsamplingpoints: "",
          samplingpoints: [],
          deviation: "",
          visitdescription: "",
        });
      }
      setDeletedVisitIds([]);
    }
  }, [existingData, reset]);

  useEffect(() => {
    if (existingData?.samplingpoints) {
      setSamplingPointsUI(
        Array.from(
          { length: Number(existingData.samplingpoints.length) },
          (_, i) => i + 1,
        ),
      );
      setValue("samplingpoints", existingData.samplingpoints);
    }
  }, [existingData]);

  const periodRows = periodFields.map((field, index) => ({
    id: field.id,
    index,
  }));

  /* ---------- Sync sampling points UI with totalsamplingpoints ---------- */

  useEffect(() => {
    if (totalSamplingPoints && String(totalSamplingPoints).length > 2) {
      const sliced = Number(String(totalSamplingPoints).slice(0, 2));
      setValue("totalsamplingpoints", sliced as any, { shouldValidate: true });
      return;
    }

    if (!totalSamplingPoints) return;
    setSamplingPointsUI(
      Array.from({ length: totalSamplingPoints }, (_, i) => i + 1),
    );
    // if no existing values → init empty array
    if (!getValues("samplingpoints")) {
      setValue("samplingpoints", []);
    } else {
      const filterSamplingPoints = getValues("samplingpoints").slice(
        0,
        totalSamplingPoints,
      );
      setValue("samplingpoints", filterSamplingPoints);
    }
  }, [totalSamplingPoints]);

  useEffect(() => {
    if (!numPeriods) return;

    const currentLength = periodFields.length;

    // ✅ ADD new periods
    if (numPeriods > currentLength) {
      const newItems = Array.from(
        { length: numPeriods - currentLength },
        (_, i) => ({
          period_no: currentLength + i + 1,
          start_date: "",
          checkin_date: "",
          checkout_date: "",
        }),
      );

      append(newItems);
    }

    // ✅ REMOVE extra periods
    if (numPeriods < currentLength) {
      for (let i = currentLength - 1; i >= numPeriods; i--) {
        remove(i);
      }
    }
  }, [numPeriods]);

  const watchedPeriods = watch("periods");
  const washoutDaysVal = watch("washoutdays");

  useEffect(() => {
    if (!watchedPeriods || !watchedPeriods.length) return;
    const washout = Number(washoutDaysVal) || 0;

    for (let i = 0; i < watchedPeriods.length; i++) {
      const currentStart = watchedPeriods[i]?.start_date;
      const currentCheckin = watchedPeriods[i]?.checkin_date;
      const currentCheckout = watchedPeriods[i]?.checkout_date;

      // 1. If Start Date is empty, checkin and checkout must be empty
      if (!currentStart) {
        if (currentCheckin) {
          setValue(`periods.${i}.checkin_date` as any, "");
          trigger(`periods.${i}.checkin_date` as any);
        }
        if (currentCheckout) {
          setValue(`periods.${i}.checkout_date` as any, "");
          trigger(`periods.${i}.checkout_date` as any);
        }
      }

      // 2. If Check-in is empty, checkout must be empty
      if (!currentCheckin && currentCheckout) {
        setValue(`periods.${i}.checkout_date` as any, "");
        trigger(`periods.${i}.checkout_date` as any);
      }

      // 3. For period i > 0: check previous checkout date and handle autofill or clear
      if (i > 0) {
        const prevCheckoutVal = watchedPeriods[i - 1]?.checkout_date;

        if (prevCheckoutVal) {
          // Autofill Start Date logic
          const minDate = parseLocalDate(prevCheckoutVal);
          if (minDate && !isNaN(minDate.getTime())) {
            minDate.setDate(minDate.getDate() + washout + 1);
            const yyyy = minDate.getFullYear();
            const mm = String(minDate.getMonth() + 1).padStart(2, "0");
            const dd = String(minDate.getDate()).padStart(2, "0");
            const formattedMinDate = `${yyyy}-${mm}-${dd}`;

            const currentStartParsed = parseLocalDate(currentStart);
            if (!currentStart || (currentStartParsed && currentStartParsed.getTime() < minDate.getTime())) {
              setValue(`periods.${i}.start_date` as any, formattedMinDate);
              trigger(`periods.${i}.start_date` as any);
            }
          }
        } else {
          // Previous checkout is cleared/empty: clear current period's Start, Check-in, and Check-out
          if (currentStart) {
            setValue(`periods.${i}.start_date` as any, "");
            trigger(`periods.${i}.start_date` as any);
          }
          if (currentCheckin) {
            setValue(`periods.${i}.checkin_date` as any, "");
            trigger(`periods.${i}.checkin_date` as any);
          }
          if (currentCheckout) {
            setValue(`periods.${i}.checkout_date` as any, "");
            trigger(`periods.${i}.checkout_date` as any);
          }
        }
      }
    }
  }, [washoutDaysVal, watchedPeriods]);

  /* ---------- Fields ---------- */

  const agemin = watch("agemin");
  const agemax = watch("agemax");
  const bmimin = watch("bmimin");
  const bmimax = watch("bmimax");
  const testdrugname = watch("testdrugname");
  const testmanufacturingdate = watch("testmanufacturingdate");
  const refdrugname = watch("refdrugname");
  const refmanufacturingdate = watch("refmanufacturingdate");
  const testDrugRequired = !!String(testdrugname || "").trim();
  const refDrugRequired = !!String(refdrugname || "").trim();

  useEffect(() => {
    if (agemin === null || agemin === undefined || agemin === "") {
      setValue("agemax", "");
      clearErrors("agemax");
      return;
    }
  }, [agemin, agemax]);

  useEffect(() => {
    if (bmimin === null || bmimin === undefined || bmimin === "") {
      setValue("bmimax", "");
      clearErrors("bmimax");
      return;
    }
  }, [bmimin, bmimax]);

  useEffect(() => {
    if (!testDrugRequired) {
      testDrugFieldNames.forEach((fieldName) => {
        unregister(fieldName as any, { keepValue: true });
        clearErrors(fieldName as any);
      });
    }
  }, [testDrugRequired]);

  useEffect(() => {
    if (!refDrugRequired) {
      refDrugFieldNames.forEach((fieldName) => {
        unregister(fieldName as any, { keepValue: true });
        clearErrors(fieldName as any);
      });
    }
  }, [refDrugRequired]);

  /* ---------- Submit ---------- */

  const COMMON_UPDATE_FIELDS = ["studyid", "updatedby", "createdby", "userid"]

  const onSubmit = async (values: StudyFormValues): Promise<void> => {
    const commonFields = COMMON_UPDATE_FIELDS.reduce((acc, key) => {
      if ((existingData as any)?.[key] !== undefined) {
        acc[key] = (existingData as any)[key];
      }
      return acc;
    }, {} as Record<string, any>);

    if (activeSection === "Checklist Assignment") {
      const checklistPayload = buildStudyChecklistsPayload(values, rawChecklistData);
      onSave({ ...commonFields, ...checklistPayload });
      return;
    }

    if (activeSection === "CRF Assignment") {
      const crfPayload = buildStudyCRFsPayload(values);
      onSave({ ...commonFields, ...crfPayload });
      return;
    }

    if (activeSection === "Ambulatory Visit") {
      const isValidVisit = await triggerVisit();
      if (!isValidVisit) return;

      const visitValues = getVisitValues();
      const hasVisit = !!visitValues.visitname;

      const newSpList: string[] = Array.isArray(visitValues.samplingpoints)
        ? visitValues.samplingpoints.filter(Boolean).map((sp: any) => String(sp).trim())
        : (visitValues.samplingpoints ? [String(visitValues.samplingpoints).trim()] : []);

      const visitsPayload: any[] = [];
      const existingVisits = existingData?.visits || [];

      if (hasVisit) {
        visitsPayload.push({
          ...(existingVisits.length > 0 ? { visitid: existingVisits[0].visitid } : {}),
          visitname: visitValues.visitname,
          visittype: Number(visitValues.visittype),
          visitcategory: Number(visitValues.visitcategory),
          samplingpoints: newSpList,
          visitwindowdeviation: visitValues.deviation || "",
          visitdescription: visitValues.visitdescription || "",
        });
      }

      const payload = {
        ...commonFields,
        deletedvisits: deletedVisitIds,
        visits: visitsPayload,
      };
      onSave(payload);
      return;
    }

    const changedFields = getChangedFields(values, existingData || {});

    onSave({ ...commonFields, ...changedFields });
  };
  const samplingErrors = errors.samplingpoints as any;
  /* ---------- Render ---------- */

  return (
    <>
      <style>{`
        .dropdown-menu-end {
          z-index: 2000 !important;
        }
      `}</style>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* ---------- Investigational Product ---------- */}
        {activeSection === "Investigational Product" && (
          <>
            <h6 className="text-primary mb-3">Test Drug</h6>
            <CommonForm
              key={testDrugRequired ? "testDrugRequired" : "testDrugOptional"}
              fields={dynamicFieldsEdit(agemin, bmimin, activeSection, displaySequenceOptions, displayAliquoteSequenceOptions, testmanufacturingdate, refmanufacturingdate).filter((f) =>
                testDrugFieldNames.includes(f.name as string),
              ).map((field) => ({
                ...field,
                required: testDrugRequired,
                rules: testDrugRequired ? field.rules : undefined,
              }))}
              control={control}
              errors={errors as FieldErrors}
              columns={1}
            />
            <hr />
            <h6 className="text-primary mb-3 mt-4">Reference Drug</h6>
            <CommonForm
              key={refDrugRequired ? "refDrugRequired" : "refDrugOptional"}
              fields={dynamicFieldsEdit(agemin, bmimin, activeSection, displaySequenceOptions, displayAliquoteSequenceOptions, testmanufacturingdate, refmanufacturingdate).filter((f) =>
                refDrugFieldNames.includes(f.name as string),
              ).map((field) => ({
                ...field,
                required: field.name !== "refdrugname" && refDrugRequired,
                rules: field.name !== "refdrugname" && refDrugRequired ? field.rules : undefined,
              }))}
              control={control}
              errors={errors as FieldErrors}
              columns={1}
            />
          </>
        )}

        {/* ---------- Study Timeline ---------- */}
        {activeSection === "Study Timeline" && (
          <>
            <CommonForm
              fields={dynamicFieldsEdit(agemin, bmimin, activeSection, displaySequenceOptions, displayAliquoteSequenceOptions, testmanufacturingdate, refmanufacturingdate)}
              control={control}
              errors={errors as FieldErrors}
              columns={1}
            />

            <CommonTableIndexWise
              columns={periodColumnsEdit(watch, control, errors, trigger)}
              data={periodRows}
              showStatus={false}
              hideAction={true}
              maxHeight={"200px"}
              // scrollable
              loading={false}
              currentPage={1}
              totalCount={periodRows.length}
              onPageChange={() => { }}
            />
          </>
        )}

        {activeSection === "Sample Collection" && (
          <>
            <CommonForm
              fields={dynamicFieldsEdit(agemin, bmimin, activeSection, displaySequenceOptions, displayAliquoteSequenceOptions, testmanufacturingdate, refmanufacturingdate).filter((f) =>
                [
                  "biologicalmatrix",
                  "totalsamplingpoints",
                  "sampleprocessingmethod",
                  "storagetemperature",
                ].includes(f.name as string),
              )}
              control={control}
              errors={errors}
              columns={1}
            />

            <Row className="mt-3">
              {(samplingpointsUI || []).length > 0 && (
                <Col xs={12} className="mb-2">
                  <div className="alert alert-info d-flex align-items-center py-2 px-3 small" role="alert">
                    <i className="ri-information-line me-2 fs-16" />
                    <div>
                      <strong>Note:</strong> Enter timepoints in standard format (e.g., <code>-01.00</code>, <code>00.00</code>, <code>01.00</code>, <code>01.50</code>).
                    </div>
                  </div>
                </Col>
              )}
              {(samplingpointsUI || []).map((_, index) => (
                <Col key={index} xs="6" md="3" lg="4" className="mb-3">
                  <Label className="w-100 text-center fw-semibold">
                    Timepoint {index + 1} <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name={`samplingpoints.${index}`}
                    control={control}
                    rules={{
                      required: `Timepoint ${index + 1} is required`,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="e.g. 01.00"
                        className="text-center"
                        invalid={!!samplingErrors?.[index]}
                      />
                    )}
                  />
                </Col>
              ))}
            </Row>
          </>
        )}

        {/* ---------- Default Sections ---------- */}
        {activeSection !== "Investigational Product" &&
          activeSection !== "Study Timeline" &&
          activeSection !== "Sample Collection" &&
          activeSection !== "Ambulatory Visit" &&
          activeSection !== "Checklist Assignment" &&
          activeSection !== "CRF Assignment" && (
            <CommonForm
              fields={dynamicFieldsEdit(agemin, bmimin, activeSection, displaySequenceOptions, displayAliquoteSequenceOptions, testmanufacturingdate, refmanufacturingdate)}
              control={control}
              errors={errors as FieldErrors}
              columns={1}
              setValue={setValue}
              getValues={getValues}
            />
          )}

        {/* ---------- Checklist Assignment Section ---------- */}
        {activeSection === "Checklist Assignment" && (
          <ChecklistAssignmentStep
            control={control}
            setValue={setValue}
            watch={watch}
            rawChecklistData={rawChecklistData}
            checklistLoading={checklistMasterQuery.isLoading}
            existingStudyChecklists={(existingData as any)?.studychecklists || []}
          />
        )}

        {/* ---------- CRF Assignment Section ---------- */}
        {activeSection === "CRF Assignment" && (
          <CRFAssignmentStep
            control={control}
            setValue={setValue}
            watch={watch}
            existingStudyCRFs={(existingData as any)?.studycrfs || []}
            studyCRFAssignmentQuery={studyCRFAssignmentQuery}
          />
        )}

        {/* ---------- Ambulatory Visit Section ---------- */}
        {activeSection === "Ambulatory Visit" && (
          <div>
            <CommonForm
              fields={ambulatoryFormFields}
              control={visitControl}
              errors={visitErrors}
              columns={2}
              setValue={setVisitValue}
              getValues={getVisitValues}
            />

            <Row className="mt-3">
              {(ambulatorySamplingPointsUI || []).length > 0 && (
                <Col xs={12} className="mb-2">
                  <div className="alert alert-info d-flex align-items-center py-2 px-3 small" role="alert">
                    <i className="ri-information-line me-2 fs-16" />
                    <div>
                      <strong>Note:</strong> Enter sampling points in standard format (e.g., <code>24</code>, <code>48</code>, <code>72</code>).
                    </div>
                  </div>
                </Col>
              )}
              {(ambulatorySamplingPointsUI || []).map((_, index) => (
                <Col key={index} xs="6" sm="4" md="3" className="mb-3">
                  <Label className="w-100 text-center fw-semibold text-nowrap">
                    Sampling Point {index + 1} <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name={`samplingpoints.${index}`}
                    control={visitControl}
                    rules={{
                      required: `Sampling Point ${index + 1} is required`,
                      validate: {
                        pattern: (val) => {
                          if (val !== "" && val !== undefined && val !== null) {
                            if (!/^[0-9]+$/.test(String(val))) {
                              return "Only numbers are allowed";
                            }
                          }
                          return true;
                        },
                        greaterThan: (val) => {
                          if (index > 0 && val !== "" && val !== undefined && val !== null) {
                            const prevVal = getVisitValues(`samplingpoints.${index - 1}`);
                            if (prevVal !== "" && prevVal !== undefined && !isNaN(Number(val)) && !isNaN(Number(prevVal))) {
                              if (Number(val) <= Number(prevVal)) {
                                return `Must be greater than\nthe previous point`;
                              }
                            }
                          }
                          return true;
                        }
                      }
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="e.g. 24"
                        className="text-center"
                        invalid={!!(visitErrors?.samplingpoints as any)?.[index]}
                        onChange={(e) => {
                          field.onChange(e);
                          const val = e.target.value;
                          if (val !== "" && val !== undefined) {
                            triggerVisit(`samplingpoints.${index}` as any);
                            if (getVisitValues(`samplingpoints.${index + 1}`)) {
                              triggerVisit(`samplingpoints.${index + 1}` as any);
                            }
                          } else {
                            clearVisitErrors(`samplingpoints.${index}` as any);
                            if (getVisitValues(`samplingpoints.${index + 1}`)) {
                              triggerVisit(`samplingpoints.${index + 1}` as any);
                            }
                          }
                        }}
                      />
                    )}
                  />
                  {(visitErrors?.samplingpoints as any)?.[index] && (
                    <FormFeedback className="d-block text-center" style={{ position: "static", width: "100%", whiteSpace: "pre-line", fontSize: "11px", lineHeight: "1.2", textAlign: "center" }}>
                      {(visitErrors?.samplingpoints as any)[index]?.message}
                    </FormFeedback>
                  )}
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* ---------- Actions ---------- */}
        <div className="d-flex justify-content-end mt-4">
          <Button
            type="button"
            color="dark"
            onClick={onCancel}
            outline
            className="me-2"
          >
            Cancel
          </Button>

          <Button type="submit" color="primary" outline disabled={isSubmitting}>
            {isSubmitting && <Spinner size="sm" className="me-2" />}
            Update
          </Button>
        </div>
      </form>
    </>
  );
};

export default EditStudyForm;
